import React, { useEffect, useState } from 'react';
import { getSummary, getGaps } from '../../services/miraklFeedService';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

const GAP_TYPES = [
  ['all', 'All SKUs'], ['incomplete', 'Incomplete required fields'],
  ['missing_master', 'Missing in item master'], ['not_in_offers', 'In feed, no offer'],
];

export default function MiraklFeedHealth() {
  const [summary, setSummary] = useState([]);
  const [marketplace, setMarketplace] = useState('');
  const [gapType, setGapType] = useState('incomplete');
  const [gaps, setGaps] = useState([]);
  const [error, setError] = useState('');
  const [gapsError, setGapsError] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [gapsLoading, setGapsLoading] = useState(false);

  useEffect(() => {
    setSummaryLoading(true);
    getSummary()
      .then(rows => { setSummary(rows); if (rows[0]) setMarketplace(rows[0].marketplace); })
      .catch(() => setError('Failed to load Mirakl feed summary'))
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => {
    if (!marketplace) return;
    setGapsLoading(true);
    getGaps(marketplace, gapType)
      .then(rows => { setGaps(rows); setGapsError(''); })
      .catch(() => { setGaps([]); setGapsError('Failed to load gap rows'); })
      .finally(() => setGapsLoading(false));
  }, [marketplace, gapType]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Mirakl Feed Health
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {summary.map(s => (
            <Card
              key={s.marketplace}
              variant="outlined"
              sx={{
                minWidth: 240,
                border: s.marketplace === marketplace ? '2px solid' : '1px solid',
                borderColor: s.marketplace === marketplace ? 'primary.main' : 'divider',
                boxShadow: s.marketplace === marketplace ? 3 : 0
              }}
            >
              <CardActionArea onClick={() => setMarketplace(s.marketplace)}>
                <CardContent>
                  <Typography variant="subtitle1" component="div" fontWeight="bold">
                    {s.marketplace}
                  </Typography>
                  <Typography variant="body2">Snapshot: {s.snapshot_date}</Typography>
                  <Typography variant="body2">SKUs in feed: {s.skus_in_feed}</Typography>
                  <Typography variant="body2">
                    Avg completeness: {Math.round((s.avg_completeness || 0) * 100)}%
                  </Typography>
                  <Typography variant="body2">
                    Missing in item master: {s.missing_item_master}
                  </Typography>
                  {s.imports && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Last accepted import: {s.imports.last_complete || 'n/a'}<br />
                      Pending (SENT): {s.imports.pending_sent} &middot; Last rejects: {s.imports.last_rejected ?? 'n/a'}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}

      <Box sx={{ my: 2 }}>
        <ToggleButtonGroup
          value={gapType}
          exclusive
          onChange={(e, value) => { if (value) setGapType(value); }}
          size="small"
        >
          {GAP_TYPES.map(([value, label]) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {gapsError && <Alert severity="error" sx={{ mb: 2 }}>{gapsError}</Alert>}

      {gapsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Showing {gaps.length} rows{gaps.length === 500 ? ' (first 500 — server cap)' : ''}
          </Typography>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>UPC</TableCell>
                  <TableCell>Feed</TableCell>
                  <TableCell>Offer</TableCell>
                  <TableCell>Item master</TableCell>
                  <TableCell>Offer state</TableCell>
                  <TableCell>Required filled</TableCell>
                  <TableCell>Missing required</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gaps.map(g => (
                  <TableRow key={g.shop_sku}>
                    <TableCell>{g.shop_sku}</TableCell>
                    <TableCell>{g.upc}</TableCell>
                    <TableCell>{g.in_feed ? 'Y' : 'N'}</TableCell>
                    <TableCell>{g.in_offers ? 'Y' : 'N'}</TableCell>
                    <TableCell>{g.in_item_master ? 'Y' : 'N'}</TableCell>
                    <TableCell>{g.offer_state}</TableCell>
                    <TableCell>{g.required_filled}/{g.required_total}</TableCell>
                    <TableCell sx={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {g.missing_required}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
